import { Box, CircularProgress, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import { useAppStore } from "../../../../hooks/useStores";
import { BulkUploadStatus } from "../../../../pages/NewTG/Datasets/store/tg-dataset-store";
import styles from "./AdvancedImageLoading.module.scss";

type Props = {
	datasetId: number;
	onCancel: () => void;
	onCompleted: (status: BulkUploadStatus) => void;
	onError?: () => void;
	initialStatus?: BulkUploadStatus;
};

const AdvancedImageLoading = ({ datasetId, onCancel, onCompleted, onError, initialStatus }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "advancedUploadPanels.advancedUpload.loading" });
	const { tgStore } = useAppStore();
	const { tgDatasetStore } = tgStore;
	const [status, setStatus] = useState<BulkUploadStatus>(
		initialStatus || { status: "pending", total_processed: 0, done: 0, failed: 0 },
	);
	const statusRef = useRef<BulkUploadStatus>(initialStatus || status);
	const pollerRef = useRef<any>(null);

	useEffect(() => {
		const fetchOnce = async () => {
			try {
				const resp = await tgDatasetStore.getBulkImagesUploadStatus(datasetId);
				const newStatus: BulkUploadStatus = resp as any;
				if (newStatus.status === "failed") throw Error();
				statusRef.current = newStatus;
				setStatus(newStatus);
				if (newStatus.status === "completed") {
					clearInterval(pollerRef.current);
					onCompleted(newStatus);
				}
			} catch (e) {
				if (pollerRef.current) clearInterval(pollerRef.current);
				onError?.();
			}
		};
		fetchOnce();
		pollerRef.current = setInterval(fetchOnce, 10000);

		return () => {
			if (pollerRef.current) clearInterval(pollerRef.current);
		};
	}, []);

	const finishedCount = (status.done || 0) + (status.failed || 0);
	const total = status.total_processed || 0;
	const pct = status.progress_percentage ?? (total ? Math.round((finishedCount / total) * 100) : 0);

	return (
		<Box className={styles.container} style={{ ["--progress" as any]: `${pct}%` }}>
			<CircularProgress size={40} className={styles.spinner} />
			{status.status === "pending" ? (
				<Typography className={styles.counterText}>{t("pendingMessage")}</Typography>
			) : (
				<Typography className={styles.counterText}>
					{t("imagesUploaded", { processed: finishedCount, total })}
				</Typography>
			)}
			<Typography className={styles.note}>
				<Typography component={"span"} className={`${styles.note} ${styles.boldNote}`}>
					{t("leaveTabOpenSection1")}
				</Typography>
				{t("leaveTabOpenSection2")}
			</Typography>
			<BriaButton buttonType={"textMedium"} onClick={onCancel} className={styles.cancelBtn}>
				{t("cancelUpload")}
			</BriaButton>
		</Box>
	);
};

export default observer(AdvancedImageLoading);
